/*
Copyright © 2021 EPAM Systems, Inc. All Rights Reserved.
All information contained herein is, and remains the property of EPAM Systems, Inc.
and/or its suppliers and is protected by international intellectual property law.
Dissemination of this information or reproduction of this material is strictly
forbidden, unless prior written permission is obtained from EPAM Systems, Inc
*/
const TODAY = 'сегодня';
const TOMORROW = 'завтра';
const getDayRegexp = /[.]{1}[0-9]{2}[.]{1}[0-9]{4}$/;
const getYearRegexp = /^([0-9]{2}[.]){2}/;
export const SERVER_TIMEZONE = 'Europe/Minsk';
export const SERVER_TIMEZONE_NAME = 'GMT+3';

export const pad = (x, size = 2) => {
    let s = String(x);
    while (s.length < size) {
        s = `0${s}`;
    }

    return s;
};

export const getTimezoneOffsetFromServer = (timeZone = SERVER_TIMEZONE, date = new Date()) => {
    try {
        const invdate = new Date(
            date.toLocaleString('en-US', { timeZone }),
        );

        const diff = date.getTime() - invdate.getTime();

        return Math.round(diff / 60000) / 60;
    } catch (e) {
        return 0;
    }
};

export const getTimezoneDiffName = () => (getTimezoneOffsetFromServer() ? SERVER_TIMEZONE_NAME : '');

let currentYear = new Date().getFullYear();

export const years = Array.from({ length: 100 }, () => `${currentYear--}`);

export const dateLocales = {
    ru: {
        monthNames: [
            'Январь',
            'Февраль',
            'Март',
            'Апрель',
            'Май',
            'Июнь',
            'Июль',
            'Август',
            'Сентябрь',
            'Октябрь',
            'Ноябрь',
            'Декабрь',
        ],
        monthNamesShort: [ 'Янв', 'Фев', 'Март', 'Апр', 'Май', 'Июня', 'Июля', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек' ],
        monthNamesDeclination: [
            'Января',
            'Февраля',
            'Марта',
            'Апреля',
            'Мая',
            'Июня',
            'Июля',
            'Августа',
            'Сентября',
            'Октября',
            'Ноября',
            'Декабря',
        ],
        dayNames: [ 'Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота' ],
        dayNamesShort: [ 'ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ' ],
    },
};
export const months = dateLocales.ru.monthNamesShort;

export const getMonthName = (d, mode = '') => {
    if (!d) {
        return '';
    }
    const isValidMode = dateLocales.ru[`monthNames${mode}`];

    return isValidMode ? isValidMode[d.getMonth()] : dateLocales.ru.monthNames[d.getMonth()];
};

export const adjustTimeZone = (d, tz) => {
    const diff = tz + d.getTimezoneOffset();
    if (diff) {
        return new Date(d.getTime() + diff * 60 * 1000);
    }
    return d;
};

export function parseISO8601String(x) {
    if (typeof x !== 'string') {
        throw new Error(`parseISO8601String: not a string: ${x}`);
    }
    if (x.length < 11) {
        // eslint-disable-next-line no-param-reassign
        x += 'T12:00';
    }
    const timebits = /^([0-9]{4})-([0-9]{2})-([0-9]{2})[ T]([0-9]{2}):([0-9]{2})(?::([0-9]*)(\.[0-9]*)?)?Z?(?:([+-])([0-9]{2})([0-9]{2}))?/;
    const m = timebits.exec(`${x}`);
    if (!m) {
        return null;
    }
    // utcdate is milliseconds since the epoch
    const utcdate = Date.UTC(
        parseInt(m[1]),
        parseInt(m[2]) - 1, // months are zero-offset (!)
        parseInt(m[3]),
        parseInt(m[4]),
        parseInt(m[5]), // hh:mm
        (m[6] && parseInt(m[6])) || 0, // optional seconds
        (m[7] && parseFloat(m[7])) || 0,
    );
    // optional timezone offset
    if (m[9] && m[10]) {
        const offsetMinutes = parseInt(m[9]) * 60 + parseInt(m[10]);
        return new Date(utcdate + (m[8] === '+' ? -60000 : +60000) * offsetMinutes);
    }
    return new Date(utcdate);
}

export const getNewAdjustedDate = (...date) => {
    if (date && date.length === 1) {
        return adjustTimeZone(new Date(...date), 0);
    }
    if (date && date.length > 1) {
        return new Date(...date);
    }
    return adjustTimeZone(new Date(), 180);
};

export const toDate = (x) => {
    const type = typeof x;
    if (x == null) {
        return null;
    }
    if (type === 'number') {
        return new Date(x);
    }
    if (type === 'object') {
        // Date-like
        if (x.getTime) {
            return x;
        }
        // firestore.Timestamp
        if (x.toDate) {
            return x.toDate();
        }
        // firestore timestamp for web
        if (x.seconds && x.nanoseconds != null) {
            return new Date(x.seconds * 1000 + x.nanoseconds);
        }
    }
    return getNewAdjustedDate(parseISO8601String(x));
};

export const formatFullDateTimezone = (x) => {
    if (!x) {
        return '';
    }
    const date = toDate(x);
    const year = String(date.getFullYear())
        .padStart(4, '0');
    const month = String(date.getMonth() + 1)
        .padStart(2, '0');
    const day = String(date.getDate())
        .padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const formatFullDateToday = d => formatFullDateTimezone(d ? new Date(d) : new Date());

export const formatFullDateEndOfMonth = (date) => {
    const d = toDate(date || Date.now());
    const year = d.getFullYear();
    const month = d.getMonth();
    const endOfMonth = new Date(year, month + 1, 0);
    return formatFullDateTimezone(endOfMonth);
};

// be careful when add 24 hours https://stackoverflow.com/questions/1197928/how-to-add-30-minutes-to-a-javascript-date-object
export const addMinutes = (d, min) => {
    if (!d) {
        return '';
    }
    const date = toDate(d);
    return new Date(date.getTime() + min * 60000);
};

export const getDayName = (d, mode = '') => {
    if (!d) {
        return '';
    }
    const dd = toDate(d);
    const isValidMode = dateLocales.ru[`dayNames${mode}`];

    return isValidMode ? isValidMode[dd.getDay()] : dateLocales.ru.dayNames[dd.getDay()];
};

/**
 * Universal all-weather converter to Date.
 *
 * @returns Date instance or null
 * @param v
 */
const getValueWithFirstZeroIfValueSmallerTen = v => (v < 10 ? `0${v}` : v);

// return date in format dd.mm.yyyy
// eslint-disable-next-line max-statements
export const formatDate = (x) => {
    if (!x) {
        return '';
    }
    if (x instanceof Array) {
        if (!x[0] || !x[1]) {
            return '';
        }
        const startDate = toDate(x[0]);
        const startDateYear = startDate.getFullYear();
        const startDateDay = startDate.getDate();
        const startDateMonth = startDate.getMonth() + 1;
        const endDate = toDate(x[1]);
        const endDateDay = endDate.getDate();
        const endDateYear = endDate.getFullYear();
        const endDateMonth = endDate.getMonth() + 1;
        return `${getValueWithFirstZeroIfValueSmallerTen(startDateDay)}.${getValueWithFirstZeroIfValueSmallerTen(
            startDateMonth,
        )}.${startDateYear} - ${getValueWithFirstZeroIfValueSmallerTen(
            endDateDay,
        )}.${getValueWithFirstZeroIfValueSmallerTen(endDateMonth)}.${endDateYear}`;
    }
    const date = toDate(x);
    const day = date.getDate();
    const dayWithZero = getValueWithFirstZeroIfValueSmallerTen(day);
    const month = date.getMonth() + 1;
    const monthWithZero = getValueWithFirstZeroIfValueSmallerTen(month);
    const year = date.getFullYear();
    return `${dayWithZero}.${monthWithZero}.${year}`;
};

export const formatDateServerTz = (x) => {
    if (!x) {
        return '';
    }
    const date = new Date(toDate(x)
        ?.toLocaleString('en-US', { timeZone: SERVER_TIMEZONE }));
    const day = date.getDate();
    const dayWithZero = getValueWithFirstZeroIfValueSmallerTen(day);
    const month = date.getMonth() + 1;
    const monthWithZero = getValueWithFirstZeroIfValueSmallerTen(month);
    const year = date.getFullYear();
    return `${dayWithZero}.${monthWithZero}.${year}`;
};

export const getFormatDateOrBlankDisplay = date => (date ? formatDate(date) : '——.——.——');

export const formatDateWithDayName = (x, dayNameMode = '') => `${formatDate(x)}, ${getDayName(x, dayNameMode)}`;

// return date in format yyyy-mm-dd
export const formatFullDate = (x) => {
    if (!x) {
        return '';
    }
    const date = toDate(x);
    const day = date.getDate();
    const dayWithZero = day < 10 ? `0${day}` : day;
    const month = date.getMonth() + 1;
    const monthWithZero = month < 10 ? `0${month}` : month;
    const year = date.getFullYear();
    return `${year}-${monthWithZero}-${dayWithZero}`;
};

// return date in format dd-mm-yyyy
export const parseToDdMmYyyy = (date) => {
    if (!date || typeof date === 'number') {
        return '';
    }
    return date
        .split('-')
        .reverse()
        .join('-');
};

// for schedule table
export const generateTimeslots = ({
    start = 8,
    end = 22,
    step = 5,
    date,
    fullDay = false,
}) => {
    const day = date ? (Array.isArray(date) && new Date(date[0])) || new Date(date) : new Date();
    fullDay ? day.setHours(0, 0, 0, 0) : day.setHours(start, 0, 0, 0);
    const arr = [new Date(day).getTime()];
    const stepInTics = step * 60 * 1000;
    if (fullDay) {
        const nextDay = new Date(day).setDate(new Date(day).getDate() + 1);
        for (let i = 0; arr[i] < nextDay - stepInTics; i++) {
            arr.push(arr[i] + stepInTics);
        }
    } else {
        const endTimeInTicks = new Date(day).setHours(end, 0, 0, 0);
        for (let i = 0; arr[i] < endTimeInTicks - stepInTics; i++) {
            arr.push(arr[i] + stepInTics);
        }
    }
    return arr;
};

export const dateLongWithDayName = (x, dayNameMode = '') => {
    if (!x) {
        return '';
    }
    if (x instanceof Array) {
        const startDate = toDate(x[0]);
        const startDateYear = startDate.getFullYear();
        const startDateDay = startDate.getDate();
        const startDateMonth = getMonthName(startDate, 'Declination');
        const endDate = toDate(x[1]);
        const endDateDay = endDate.getDate();
        const endDateYear = endDate.getFullYear();
        const endDateMonth = getMonthName(endDate, 'Declination');
        return `${startDateDay} ${startDateMonth} ${startDateYear} - ${endDateDay} ${endDateMonth} ${endDateYear}`;
    }
    const date = toDate(x);
    const visitYear = date.getFullYear();
    const day = date.getDate();
    const monthName = getMonthName(date, 'Declination');
    const dayName = getDayName(date, dayNameMode);
    return `${day} ${monthName} ${visitYear}, ${dayName}`;
};

export const formatTime = (x) => {
    if (!x) {
        return '';
    }
    const date = toDate(x);
    const hours = pad(date.getHours());
    const minutes = date.getMinutes();
    return `${hours}:${pad(minutes)}`.trim();
};

export const formatTimeServerTz = (x) => {
    if (!x) {
        return '';
    }
    const date = new Date(toDate(x)
        ?.toLocaleString('en-US', { timeZone: SERVER_TIMEZONE }));
    const hours = pad(date.getHours());
    const minutes = date.getMinutes();
    return `${hours}:${pad(minutes)}`.trim();
};

export const formatDateLong = (x, withTime, tz, withTimezone) => {
    if (!x) {
        return '';
    }
    const date = toDate(x);
    const visitYear = date.getFullYear();
    const day = date.getDate();
    const monthName = getMonthName(date, 'Short');
    const str = `${day} ${monthName} ${visitYear}`;
    return withTime ? [ `${str}`, formatTime(date, tz, withTimezone) ].join(' в ') : str;
};

export const todayOrTomorrow = (date) => {
    if (!date) {
        return null;
    }
    const now = new Date();
    if (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()) {
        const day = date.getDate();
        const currentDay = now.getDate();
        if (day === currentDay) {
            return TODAY;
        }
        if (day === currentDay + 1) {
            return TOMORROW;
        }
    }
    return null;
};

export const formatDateShort = (x) => {
    if (!x) {
        return '';
    }
    const date = toDate(x);
    return todayOrTomorrow(date) || `${date.getDate()} ${getMonthName(date, 'Declination')}`;
};
/**
 * @param  {Date} x
 *
 * @returns date month declination // 2 февраля
 */
export const dateAndMonth = (x) => {
    if (!x) {
        return null;
    }
    const date = toDate(x);
    const monthName = dateLocales.ru.monthNamesDeclination[date.getMonth()].toLowerCase();
    return [ date.getDate(), monthName ].join(' ');
};

export const isDayNotPast = (date) => {
    if (!date) {
        return '';
    }
    const now = adjustTimeZone(new Date(), 180);
    return (
        date.getFullYear() > now.getFullYear() ||
        (date.getFullYear() === now.getFullYear() &&
            (date.getMonth() > now.getMonth() ||
                (date.getMonth() === now.getMonth() && date.getDate() >= now.getDate())))
    );
};

export const getDaysInMonth = (date) => {
    const x = new Date(date);
    return 33 - new Date(x.getFullYear(), x.getMonth(), 33).getDate();
};

export const MinskTimeZoneOffsetMinutes = 3 * 60;

export const getTimeZoneDiffMinutes = tz => (tz ? Number(tz) + new Date().getTimezoneOffset() : null);

export const formatTimezone = (tzOffset) => {
    const toNumber = Number(tzOffset);
    return toNumber
        ? `(GMT ${
            toNumber >= 0
                ? `+${pad(toNumber / 60)}:${pad(toNumber % 60)}`
                : `-${pad(-toNumber / 60)}:${pad(-toNumber % 60)}`
        })`
        : null;
};

// yyyy-mm-dd day with zero, toISOString works wrong in some cases
export const getIsoFormattedDate = (adjastedDate) => {
    const day = adjastedDate.getDate() > 9 ? adjastedDate.getDate() : `0${adjastedDate.getDate()}`;
    const month = adjastedDate.getMonth() + 1 > 9 ? adjastedDate.getMonth() + 1 : `0${adjastedDate.getMonth() + 1}`;
    return `${adjastedDate.getFullYear()}-${month}-${day}`;
};

export const timeOfDay = (startDate) => {
    const time = startDate.getHours();
    if (time <= 12) {
        return 0;
    }
    if (time > 12 && time <= 16) {
        return 1;
    }
    if (time > 16) {
        return 2;
    }
    return 0;
};

export const daysInMonth = (month, year) => (months.includes(month) && year
    ? Array.from(
        { length: new Date(Number(year), months.indexOf(month) + 1, 0).getDate() },
        (v, k) => `${k + 1}`,
    )
    : null);

export const calcEndTime = (time, duration) => {
    if (!time) {
        return '';
    }
    const date = toDate(time);
    const minutes = date.getMinutes();
    return date.setMinutes(minutes + duration);
};

export const generateDaysArray = (value) => {
    const today = new Date(new Date(value || null).setDate(1));
    const firstDayIndexOfWeekDay = today.getDay() === 0 ? 6 : today.getDay() - 1; // because our calendar start from Mon
    let counter = 1;
    const countOfDaysInMonth = today.getDaysInMonth();
    const daysArr = Array.from(
        { length: 42 },
        (v, i) => (i >= firstDayIndexOfWeekDay && counter <= countOfDaysInMonth ? counter++ : 0),
    ); // generate arr of items for calendar
    if (daysArr[35] === 0) {
        daysArr.splice(35, 7);
    }

    return [ daysArr, today ];
};

export const generateDaysForPeriod = (start, end) => {
    const firstDate = new Date(toDate(start)
        .setHours(0, 0, 0, 0));
    const lastDate = new Date(toDate(end)
        .setHours(0, 0, 0, 0));
    const dates = [new Date(firstDate)];
    while (firstDate < lastDate) {
        firstDate.setDate(firstDate.getDate() + 1);
        dates.push(new Date(firstDate));
    }
    return dates;
};

export const getDayNamesShort = () => {
    const weekArr = [...dateLocales.ru.dayNamesShort];
    return weekArr.splice(1)
        .concat(weekArr);
};

export const timeSubtract = (time, subTime) => Math.abs(new Date(time) - new Date(subTime)) / 60000;

export const createDateFromMaskInput = (value) => {
    const day = value.replace(getDayRegexp, '');
    const month = value[3] + value[4];
    const year = value.replace(getYearRegexp, '');
    return new Date(Number(year), Number(month) - 1, Number(day));
};

export const getDateToISOStringWithTimeZoneOffset = date => new Date(new Date(date).getTime() - new Date(date).getTimezoneOffset() * 60000).toISOString()
    .split('T')[0];

export const getCustomDateFromDate = (date, {
    year = 0,
    month = 0,
    day = 0,
}) => formatFullDate(
    new Date(
        new Date(date).setFullYear(
            new Date(date).getFullYear() + year,
            new Date(date).getMonth() + month,
            new Date(date).getDate() + day,
        ),
    ),
);

export const getCustomDateFromToday = ({
    year = 0,
    month = 0,
    day = 0,
}) => getCustomDateFromDate(new Date(), {
    year,
    month,
    day,
});

export const getDateFirstOfMonth = (date = new Date()) => {
    const firstOfMonth = new Date(
        new Date(date).getFullYear(),
        new Date(date).getMonth(),
        new Date(date).getDate() - new Date(date).getDate() + 1,
    );
    return formatFullDate(firstOfMonth);
};

export const getDateLastOfMonth = (date = new Date()) => {
    const lastOfMonth = new Date(
        new Date(date).getFullYear(),
        new Date(date).getMonth() + 1,
        new Date(date).getDate() - new Date(date).getDate(),
    );
    return formatFullDate(lastOfMonth);
};

export const getDateDifferentDate = (dates) => {
    if (!Array.isArray(dates)) {
        return 1;
    }
    const date1 = new Date(dates[0]);
    const date2 = new Date(dates[1]);
    const diffTime = Math.abs(date2 - date1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
};

export const makeTargetDate = (date) => {
    return date && getDateToISOStringWithTimeZoneOffset(date);
};

export const getDifferenceInDays = (a, b) => {
    if (!a || !b) {
        return null;
    }
    const aa = toDate(a);
    const bb = toDate(b);
    const differenceInTime = aa.getTime() - bb.getTime();
    return differenceInTime / (1000 * 3600 * 24);
};

/**
 * Universal all-weather converter to Date.
 *
 * @param {*} date value date start with yyyy-mm-dd
 * @param {*} time value format hh:ii
 * @returns String format yyyy-mm-ddThh:ii
 */
export const getVDateTimeByDateAndTime = (date, time) => {
    if (!date || !time) {
        return '';
    }
    const [ yyyy, mm, dd ] = date.split('-');
    const [ hh, ii ] = date.split(':');
    return `${yyyy}-${mm}-${dd}T${hh}:${ii}`;
};

export const isDayEvenByDate = date => new Date(date).getDate() % 2 === 0;

export const getAge = (dateString) => {
    const day = new Date();
    const birthDate = new Date(dateString);
    let age = day.getFullYear() - birthDate.getFullYear();
    const m = day.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && day.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

export const addMonths = (date, mnt) => {
    const monthSum = date.getMonth() + mnt;
    const newMonth = (12 + (monthSum % 12)) % 12;
    const newYear = date.getFullYear() + Math.floor(monthSum / 12);
    const newDate = new Date(newYear, newMonth, date.getDate());
    return newDate.getMonth() !== newMonth ? new Date(newDate.setDate(0)) : newDate;
};

// eslint-disable-next-line no-restricted-globals
export const isValidDateObject = d => d instanceof Date && d?.toString() !== 'Invalid Date' && !isNaN(d?.getTime?.());

export const convertTimeZone = (date, timeZoneFrom = null, timeZoneTo = null) => {
    let sourceDate = toDate(date);
    if (Number.isNaN(sourceDate) || typeof sourceDate === 'undefined' || sourceDate === null && (typeof sourceDate === 'object' && !isValidDateObject(sourceDate))) {
        sourceDate = new Date();
    } else if (typeof sourceDate === 'number') {
        sourceDate = new Date(sourceDate);
    }

    if (typeof sourceDate === 'object' && !isValidDateObject(sourceDate)) {
        sourceDate = new Date();
    }

    let dateFrom =
        timeZoneFrom == null
            ? sourceDate
            : new Date(
                sourceDate?.toLocaleString?.('en-US', { timeZone: timeZoneFrom }),
            );
    if (typeof dateFrom === 'object' && !isValidDateObject(dateFrom)) {
        dateFrom = sourceDate;
    }

    let dateTo =
        timeZoneTo == null
            ? sourceDate
            : new Date(
                sourceDate?.toLocaleString?.('en-US', { timeZone: timeZoneTo }),
            );
    if (typeof dateTo === 'object' && !isValidDateObject(dateTo)) {
        dateTo = sourceDate;
    }
    // if (!date) {
    //     return undefined;
    // }
    // if (!date.getTime) {
    //     date = toDate(date);
    // }
    //
    // if (!date?.getTime) {
    //     return undefined;
    // }

    return new Date(
        (sourceDate?.getTime?.() ?? 0) + (dateTo?.getTime?.() ?? 0) - (dateFrom?.getTime?.() ?? 0),
    );
};

export const getEndDate = (d) => {
    return addMonths(convertTimeZone(d, null, SERVER_TIMEZONE), 2);
};

export const formatTimeWithoutTimezone = (x) => {
    if (!x) {
        return '';
    }
    let date;
    try {
        date = getNewAdjustedDate(parseISO8601String(x));
    } catch (e) {
        const d = new Date(x);
        date = convertTimeZone(d, null, SERVER_TIMEZONE);
    }
    const hours = pad(date.getHours());
    const minutes = date.getMinutes();

    return `${hours}:${pad(minutes)}`.trim();
};

export const isSameDate = (date1, date2) => {
    if (date1 && date2) {
        const [date] = date1.split(' ');
        return date === date2;
    }
    return false;
};

export const timeslotToDate = (timeSlot = '') => {
    try {
        const year = `20${timeSlot.substring(0, 2)}`;
        const month = timeSlot.substring(2, 4);
        const day = timeSlot.substring(4, 6);
        const hour = timeSlot.substring(6, 8);
        const minute = timeSlot.substring(8, 10);
        return new Date(`${year}/${month}/${day} ${hour}:${minute}:00`);
    } catch (e) {
        return timeSlot;
    }
};

export const convertToMysqlDate = (inputDate) => {
    const convertDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1)
            .padStart(2, '0');
        const day = String(date.getDate())
            .padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    if (inputDate instanceof Date && !Number.isNaN(inputDate)) {
        return convertDate(inputDate);
    }
    if (typeof inputDate === 'string') {
        const preparedString = inputDate.trim()
            .split('-')
            .join('.')
            .split('/')
            .join('.');
        const parts = preparedString.split('.');
        if (parts.length === 3) {
            if (parts[0].length === 4) {
                return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
            }
            if (parts[2].length === 4) {
                return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
        }
    } else if (typeof inputDate === 'undefined' || inputDate === null || inputDate === '') {
        return convertDate(new Date());
    }
    return inputDate;
};
